import { Injectable, OnDestroy, Optional } from '@angular/core';
import { CollabFileType } from '@common/collab/enums/collab-file-type.enum';
import { StylesheetType } from '@common/flare-data/enums/stylesheet-type.enum';
import { CssUtils, defaultMedium } from '@common/flare-data/util/css-utils';
import { isLwePreviewStyleProperty, patchSelectorForLweEditorStylesheet, patchSelectorForLwePreviewStylesheet, toLweEditorStylePropertyName } from '@common/flare-data/util/lwe-css-builder';
import { BlockTagsWithAnyContent, BlockTagsWithPhrasingContent, InlineTagsWithPhrasingContent, ListTags } from '@common/flare/constants/compatible-html-tags.constants';
import { madcapCustomAttrName } from '@common/flare/schema/custom-attributes-schema-plugin';
import { getSelectionStyle, SelectionStyle } from '@common/prosemirror/commands/selection-format.command';
import { cache } from '@common/util/cache.operator';
import { resolvePath } from '@common/util/path';
import { ProjectFileType } from '@portal-core/project-files/enums/project-file-type.enum';
import { StyleSource } from '@portal-core/project-files/models/style-source.model';
import { FactoryStyleSourcesService } from '@portal-core/project-files/services/factory-style-sources.service';
import { ProjectFilesLinkService } from '@portal-core/project-files/services/project-files-link.service';
import { ProjectFilesService } from '@portal-core/project-files/services/project-files.service';
import { ProjectStylesheetCacheService } from '@portal-core/project-files/services/project-stylesheet-cache.service';
import { ComputedStyle, ComputedStylesheet } from '@portal-core/project-files/stylesheets/models/computed-stylesheet.model';
import { CssStylesheet } from '@portal-core/project-files/stylesheets/models/css-stylesheet.model';
import { CssCode, PreviewCssStyle } from '@portal-core/project-files/stylesheets/models/preview-css-style.model';
import { ComputedStylesheetService } from '@portal-core/project-files/stylesheets/services/computed-stylesheet.service';
import { CssStylesheetService } from '@portal-core/project-files/stylesheets/services/css-stylesheet.service';
import { StyleService } from '@portal-core/project-files/stylesheets/services/style.service';
import { TextEditorService } from '@portal-core/text-editor/services/text-editor.service';
import { WorkspaceSessionService } from '@portal-core/workspaces/services/workspace-session.service';
import { isEqual } from 'lodash';
import { Root } from 'postcss';
import { ProseMirrorNode } from 'prosemirror-model';
import { EditorState } from 'prosemirror-state';
import { BehaviorSubject, buffer, combineLatest, debounceTime, distinctUntilChanged, filter, from, map, merge, Observable, of, Subject, switchMap, takeUntil, tap, timer } from 'rxjs';

/** Minimum interval between two stylesheet refreshes, to avoid refreshing too frequently when multiple file events happen in a short time. */
const CssStyleRefreshMinIntervalMs = 1000;

/** Describes document style options. */
interface DocumentStyleOptions {
  /*  Whether to use only local stylesheets defined in the document head. */
  onlyLocalStylesheets: boolean;
  /** The content of the document head tag that contains CSS stylesheet links and `<style>` tags. */
  headTagContent: string;
}

@Injectable()
export class TextEditorCssStylesService implements OnDestroy {

  private availableStylesSubject: BehaviorSubject<PreviewCssStyle[]> = new BehaviorSubject<PreviewCssStyle[]>([]);
  private applicableStylesCodeSubject: BehaviorSubject<CssCode> = new BehaviorSubject<CssCode>(null);
  /** A subject with set of computed stylesheets: [ 'editor styles', 'preview styles' ] . */
  private computedStylesheetSubject: BehaviorSubject<[ComputedStylesheet, ComputedStylesheet]> = new BehaviorSubject<[ComputedStylesheet, ComputedStylesheet]>(null);
  private documentSubject: Subject<ProseMirrorNode> = new Subject<ProseMirrorNode>();
  private mediumSubject: BehaviorSubject<string> = new BehaviorSubject<string>(defaultMedium);
  private reloadSubject: BehaviorSubject<void> = new BehaviorSubject<void>(null);
  private selectionStyleSubject: BehaviorSubject<SelectionStyle> = new BehaviorSubject<SelectionStyle>(null);
  private stylesLoadedSubject: Subject<Error | undefined> = new Subject<Error | undefined>();
  private styleSourcesSubject: BehaviorSubject<StyleSource[]> = new BehaviorSubject<StyleSource[]>(null);

  public availableStyles$: Observable<PreviewCssStyle[]> = this.availableStylesSubject.asObservable();
  public applicableStylesCode$: Observable<CssCode> = this.applicableStylesCodeSubject.asObservable();
  public generateEditorStyles: boolean = false;
  public generatePreviewStyles: boolean = false;
  public stylesLoaded$: Observable<Error | undefined> = this.stylesLoadedSubject.asObservable();
  public selectionStyle$: Observable<SelectionStyle> = this.selectionStyleSubject.asObservable();

  /** The subject to unsubscribe from observables. */
  private unsubscribe: Subject<void> = new Subject<void>();

  /** Unsubscribe subject for service lifetime subscriptions. */
  private destroyed: Subject<void> = new Subject<void>();

  private previewStylesheetId: string;
  private computedStylesheetId: string;

  /** A weak map for caching available styles that are collected each time the selection changes.  */
  private readonly availableStylesCache = new WeakMap<ComputedStylesheet, Map<string, PreviewCssStyle[]>>();
  /** A weak map for caching applicable styles code that is collected each time the selection changes.  */
  private readonly applicableStylesCodeCache = new WeakMap<ComputedStylesheet, Map<string, CssCode>>();

  constructor(
    private computedStylesheetService: ComputedStylesheetService,
    private cssStylesheetService: CssStylesheetService,
    private factoryStyleSourcesService: FactoryStyleSourcesService,
    private projectFileLinkService: ProjectFilesLinkService,
    private projectFileService: ProjectFilesService,
    private projectStylesheetCacheService: ProjectStylesheetCacheService,
    private styleService: StyleService,
    private textEditorService: TextEditorService,
    @Optional() private workspaceSessionService: WorkspaceSessionService | null,
  ) {
    if (this.workspaceSessionService) {
      // `docMerged$` only contributes when the merge won't be followed by a `fileUpdated$` for the
      // same path — i.e. when the post-merge doc has no uncommitted changes. For merges of dirty
      // docs the syncer guarantees a `workspace.file.updated` later, so we let `fileUpdated$`
      // drive the reload and skip the merge event to avoid reloading twice.
      const docMergedEvents$ = this.workspaceSessionService.docMerged$.pipe(
        filter(event => !event.hasUncommittedChanges)
      );
      const fileChangeEvents$ = merge(
        this.workspaceSessionService.fileDeleted$,
        this.workspaceSessionService.fileInserted$,
        this.workspaceSessionService.fileUpdated$,
        docMergedEvents$
      );

      fileChangeEvents$.pipe(
        // buffer events to avoid refreshing styles too frequently when multiple file events happen in a short time
        buffer(
          fileChangeEvents$.pipe(switchMap(() => timer(CssStyleRefreshMinIntervalMs)))
        ),
        map(events => events.map(event => event.filePath)),
        takeUntil(this.destroyed),
      ).subscribe((fileNames) => {
        // Check whether any changed file is used for computing styles.
        const changedFilesAreUsed = this.styleSourcesSubject.value?.some(source => fileNames.includes(source.path));
        // The flare project file (.flprj) drives which style sources are used (BrandingStylesheet, MasterStylesheet, MasterStylesheetOverride),
        // so changes to it must also trigger a reload.
        const flareProjectChanged = fileNames.some(name => this.projectFileService.getFileType(name) === ProjectFileType.Project);
        if (changedFilesAreUsed || flareProjectChanged) {
          // Cache invalidation for these same events is owned by `ProjectStylesheetCacheService` and
          // happens synchronously on each event, so by the time this debounced reload fires the
          // cache has already dropped affected entries — the reload pipeline re-fetches fresh content.
          this.reloadStyles();
        }
      });
    }
  }

  ngOnDestroy() {
    // unsubscribe from observables
    this.unsubscribe.next();
    this.unsubscribe.complete();

    this.destroyed.next();
    this.destroyed.complete();

    this.computedStylesheetSubject.complete();
    this.documentSubject.complete();
    this.mediumSubject.complete();
    this.reloadSubject.complete();
    this.selectionStyleSubject.complete();
    this.stylesLoadedSubject.complete();
    this.styleSourcesSubject.complete();

    this.styleService.remove(this.previewStylesheetId);
    this.styleService.remove(this.computedStylesheetId);
  }

  private init(projectId: number, commitIdOrBranchName: string, fileType: CollabFileType, filePath: string, doc: ProseMirrorNode) {
    const filePathHex$ = from(this.textEditorService.buildFilePathHex(filePath)).pipe(cache());

    // Load style sources and compute stylesheets when the document or project file changes
    this.getStyleSources$(projectId, commitIdOrBranchName, fileType, filePath, doc).pipe(
      takeUntil(this.unsubscribe)
    ).subscribe((styleSources) => {
      this.styleSourcesSubject.next(styleSources);
    }, error => {
      this.stylesLoadedSubject.next(error);
    });

    // Compute the stylesheet when style sources or medium changes
    combineLatest([
      this.factoryStyleSourcesService.factoryStyleSources$,
      this.styleSourcesSubject.pipe(filter(sources => !!sources)),
      this.mediumSubject
    ]).pipe(
      takeUntil(this.unsubscribe)
    ).subscribe(([factoryStyleSources, styleSources, medium]) => {
      // load factory style sources for preview styles
      const previewStyleSheets: CssStylesheet[] = [];
      if (this.generatePreviewStyles) {
        factoryStyleSources.forEach(styleSource => {
          try {
            const stylesheet = this.cssStylesheetService.load(styleSource.path, styleSource.css);
            previewStyleSheets.push(stylesheet);
          } catch (error) {
            // ignore invalid stylesheets
          }
        });
      }
      // load document style sources for editor and preview styles
      const editorStyleSheets: CssStylesheet[] = [];
      if (this.generateEditorStyles || this.generatePreviewStyles) {
        styleSources
          .filter(source => source.stylesheetType !== StylesheetType.Table) // exclude table stylesheets from computed stylesheet
          .forEach(styleSource => {
            const mediums = CssUtils.getMediums(medium);
            if (!styleSource.mediaQueries || styleSource.mediaQueries.length === 0 || styleSource.mediaQueries.some(mq => mediums.includes(mq))) {
              try {
                const stylesheet = this.cssStylesheetService.load(styleSource.path, styleSource.css);
                if (this.generateEditorStyles)
                  editorStyleSheets.push(stylesheet);
                if (this.generatePreviewStyles)
                  previewStyleSheets.push(stylesheet);
              } catch (error) {
                // ignore invalid stylesheets
              }
            }
          });
      }
      const editorComputed = this.computedStylesheetService.compute(editorStyleSheets, medium);
      const previewComputed = this.computedStylesheetService.compute(previewStyleSheets, medium);
      this.computedStylesheetSubject.next([editorComputed, previewComputed]);
    }, error => {
      this.stylesLoadedSubject.next(error);
    });

    // Update styles in the editor when the computed stylesheet is updated
    combineLatest([
      filePathHex$,
      this.computedStylesheetSubject.pipe(filter(computed => !!computed))
    ]).pipe(
      takeUntil(this.unsubscribe)
    ).subscribe(([filePathHex, [editorComputed, previewComputed]]) => {
      try {
        if (this.generateEditorStyles) {
          this.updateEditorComputedStyle(projectId, commitIdOrBranchName, filePath, filePathHex, editorComputed);
        }
        if (this.generatePreviewStyles) {
          this.updatePreviewStyle(projectId, commitIdOrBranchName, filePath, filePathHex, previewComputed);
        }
      } catch (error) {
        console.error('Error updating text editor styles:', error);
        this.stylesLoadedSubject.next(error);
        return;
      }
      this.stylesLoadedSubject.next(undefined);
    }, error => {
      this.stylesLoadedSubject.next(error);
    });

    // Update available styles when selection style or computed stylesheet changes
    combineLatest([
      this.selectionStyle$.pipe(
        filter(style => !!style),
        distinctUntilChanged((a, b) => a.selectedStyles.join(',') === b.selectedStyles.join(','))
      ),
      this.computedStylesheetSubject.pipe(
        map((computed) => (computed ? computed[1] : null) as ComputedStylesheet) // take preview computed stylesheet
      )
    ]).pipe(
      takeUntil(this.unsubscribe),
    ).subscribe(([style, stylesheet]) => {
      const availableStyles = this.getAvailableStyles(style.isInlineSelection ? ['span'] : style.selectedTags, style.selectedIds, stylesheet);
      this.availableStylesSubject.next(availableStyles);

      const applicableStylesCode = style.selectedStyles.length === 1 ? this.getApplicableStylesCode(style.selectedStyleName, stylesheet) : null;
      this.applicableStylesCodeSubject.next(applicableStylesCode);
    });
  }

  /** Updates document state to refresh document CSS stylesheet and compute a style for the current document selection. */
  public updateState(state: EditorState): void {
    if (state) {
      // update document state
      this.documentSubject.next(state.doc);
      // compute selection style
      const selectionStyle = getSelectionStyle(state);
      // emit selection style if changed
      if (!isEqual(this.selectionStyleSubject.value, selectionStyle)) {
        this.selectionStyleSubject.next(selectionStyle);
      }
    }
  }

  /** Loads all stylesheets applied to the topic. */
  public load(projectId: number, commitIdOrBranchName: string, fileType: CollabFileType, filePath: string, doc: ProseMirrorNode): void {
    this.clearStyles();
    // Terminate any existing subscriptions created by a previous load()
    this.unsubscribe.next();
    // Initialize styles loading and processing with the new file
    this.init(projectId, commitIdOrBranchName, fileType, filePath, doc);
  }

  /** Reloads styles and regenerates computed stylesheets. */
  public reloadStyles(): void {
    this.reloadSubject.next();
  }

  /** Gets available styles for the specified tags. */
  private getAvailableStyles(tags: string[], ids: string[], stylesheet: ComputedStylesheet): PreviewCssStyle[] {
    if (!stylesheet)
      return [];
    const cacheKey = this.buildStyleCacheKey(tags, ids);
    // try to get cached styles
    let styles = this.getAvailableStylesFromCache(stylesheet, cacheKey);
    if (styles) {
      return styles;
    }
    // get valid tags based on the selected tags and HTML tag categories.
    const validTags = this.getValidTags(tags);
    // compute styles for the selected tag, grouped by base selector with their pseudo/attribute variants and descendant rules
    const groups = this.computedStylesheetService.collectMatchingStyles(stylesheet, validTags, ids, { clearAttrs: true, clearPseudos: true });
    styles = groups
      .sort((a, b) => a.baseName.localeCompare(b.baseName, undefined, { sensitivity: 'base' }))
      .map(({ baseName, variantNames, descendantNames }) => {
        const variantStyles = variantNames.map(v => stylesheet.styles[v]).filter(s => !!s);
        const descendantStyles = descendantNames.map(v => stylesheet.styles[v]).filter(s => !!s);
        // base style for description: the rule keyed exactly by the base name if present, else the first variant
        const baseStyle = stylesheet.styles[baseName] ?? variantStyles[0];
        const style: PreviewCssStyle = {
          name: baseName.replace('|', ':'),  // convert the tag name with namespace from CSS format into XML format.
          previewClasses: patchSelectorForLwePreviewStylesheet(baseName).replaceAll('.', ' ').trimStart(),
          description: this.computedStylesheetService.getStyleDescription(baseStyle),
          code: {
            variants: this.computedStylesheetService.getStyleCode(variantStyles),
            descendants: this.computedStylesheetService.getStyleCode(descendantStyles)
          }
        }
        return style;
      });
    // put collected styles to cache
    this.cacheAvailableStyles(stylesheet, cacheKey, styles);
    return styles;
  }

  private getValidTags(tags: string[]): string[] {
    if (!tags?.length) {
      return [];
    }
    let hasBlockWithAnyContent = false;
    let hasBlockWithPhrasingContent = false;
    let hasInlineWithPhrasingContent = false;
    let hasList = false;
    let hasNoGroup = false;
    tags.forEach(tag => {
      if (BlockTagsWithAnyContent.includes(tag)) {
        hasBlockWithAnyContent = true;
      } else if (BlockTagsWithPhrasingContent.includes(tag)) {
        hasBlockWithPhrasingContent = true;
      } else if (InlineTagsWithPhrasingContent.includes(tag)) {
        hasInlineWithPhrasingContent = true;
      } else if (ListTags.includes(tag)) {
        hasList = true;
      } else {
        hasNoGroup = true;
      }
    });
    const hasBlock = hasBlockWithAnyContent || hasBlockWithPhrasingContent;
    const categoriesCount = [hasBlock, hasInlineWithPhrasingContent, hasList, hasNoGroup].filter(Boolean).length;
    if (categoriesCount > 1) { // When different categories of tags are selected at once, will ignore selectors with tags
      return [];
    } else if (categoriesCount === 1) { // When tags from only one category
      if (hasBlock) { // When only block tags are selected
        // If block tags with phrasing content are selected, include them.
        // Include block tags with any content in any case, because
        // they can contain the same content as block tags with phrasing content, but not vice versa.
        return hasBlockWithPhrasingContent
          ? [...BlockTagsWithAnyContent, ...BlockTagsWithPhrasingContent]
          : BlockTagsWithAnyContent;
      } else if (hasInlineWithPhrasingContent) { // When only inline tags with phrasing content are selected,
        return InlineTagsWithPhrasingContent; // return inline tags with phrasing content.
      } else if (hasList) { // When only list tags are selected,
        return ListTags; // return list tags.
      } else {
        // When a few tags that don't belong to any category are selected, return an empty array of valid tags. Otherwise return the selected tags (one or zero) as only valid tags.
        return tags.length > 2
          ? []
          : tags;
      }
    }
  }

  /** Builds a cache key for available styles by specified tag names and node IDs. */
  private buildStyleCacheKey(tags: string[], ids: string[]): string {
    const tagsKey = JSON.stringify(tags);
    const idsKey = JSON.stringify(ids);
    return `${tagsKey}|${idsKey}`;
  }

  /** Tries to get available styles for selected tags from cache. */
  private getAvailableStylesFromCache(stylesheet: ComputedStylesheet, cacheKey: string): PreviewCssStyle[] | undefined {
    const cache = this.availableStylesCache.get(stylesheet);
    return cache?.[cacheKey];
  }

  /** Caches available styles for selected tags. */
  private cacheAvailableStyles(stylesheet: ComputedStylesheet, cacheKey: string, value: PreviewCssStyle[]) {
    let cache = this.availableStylesCache.get(stylesheet);
    if (!cache) {
      cache = new Map<string, PreviewCssStyle[]>();
      this.availableStylesCache.set(stylesheet, cache);
    }
    cache[cacheKey] = value;
  }

  /** Gets available styles for the specified selection style. */
  private getApplicableStylesCode(selectedStyleName: string, stylesheet: ComputedStylesheet): CssCode | null {
    if (!stylesheet)
      return null;
    let code: CssCode = this.getApplicableStylesCodeFromCache(stylesheet, selectedStyleName);
    if (code) {
      return code;
    }
    code = { variants: [], descendants: [] };
    const selectionSelectors = CssUtils.parseSelector(selectedStyleName);
    if (selectionSelectors.length === 1) {
      const styles = this.computedStylesheetService.collectApplicableStyles(stylesheet, selectionSelectors[0], { clearAttrs: true, clearPseudos: true })
      const variantStyles = styles.variantNames.map(v => stylesheet.styles[v]).filter(s => !!s);
      const descendantStyles = styles.descendantNames.map(v => stylesheet.styles[v]).filter(s => !!s);
      code.variants = this.computedStylesheetService.getStyleCode(variantStyles);
      code.descendants = this.computedStylesheetService.getStyleCode(descendantStyles);
    }
    this.cacheApplicableStylesCode(stylesheet, selectedStyleName, code);
    return code;
  }

  /** Tries to get applicable styles code for the specified selection style from cache. */
  private getApplicableStylesCodeFromCache(stylesheet: ComputedStylesheet, styleName: string): CssCode | undefined {
    const cache = this.applicableStylesCodeCache.get(stylesheet);
    return cache?.[styleName];
  }

  /** Caches applicable styles code for the specified selection style . */
  private cacheApplicableStylesCode(stylesheet: ComputedStylesheet, styleName: string, value: CssCode) {
    let cache = this.applicableStylesCodeCache.get(stylesheet);
    if (!cache) {
      cache = new Map<string, CssCode>();
      this.applicableStylesCodeCache.set(stylesheet, cache);
    }
    cache[styleName] = value;
  }

  /** Clears current selection style. */
  private clearStyles(): void {
    this.computedStylesheetSubject.next(null);
    this.selectionStyleSubject.next({ selectedIds: [], selectedStyles: [], selectedTags: [], selectedStyleName: '' });
    this.styleSourcesSubject.next(null);
    this.availableStylesSubject.next([]);
    this.styleService.remove(this.previewStylesheetId);
    this.styleService.remove(this.computedStylesheetId);
  }

  /** Creates a style source from the specified path and content. */
  private createStyleSource(path: string, mediaQueries: string[] = null, content: string = null, basePath: string = null): StyleSource {
    try {
      let root: Root = null;
      let stylesheetType: StylesheetType = null;
      if (content) {
        stylesheetType = CssUtils.stylesheetType(content);
        if (basePath) {
          content = this.projectFileLinkService.updateCssLinks(content, path, basePath);
        }
        root = CssUtils.parse(content);
      }
      return { path, mediaQueries, css: root, stylesheetType };
    }
    catch {
      return {} as StyleSource;
    }
  }

  /** Gets document style options from the document node. */
  private getDocumentStyleOptions(doc: ProseMirrorNode): DocumentStyleOptions {
    const htmlNode = doc.firstChild;

    let onlyLocalStylesheets: boolean = false;
    const customAttr = htmlNode.attrs[madcapCustomAttrName];
    if (customAttr)
      onlyLocalStylesheets = customAttr['MadCap:onlyLocalStylesheets'] === 'True';

    let headTagContent: string = '';
    // 'MadCap:htmlStructure' attribute contains the original HTML code for the head element
    const structure = htmlNode.attrs['MadCap:htmlStructure'] as [];
    if (structure && structure.length > 0) {
      for (let el of structure) {
        if ((el as any).nodeName === 'head') {
          headTagContent = (el as any).data;
          break;
        }
      }
    }

    return { onlyLocalStylesheets, headTagContent };
  }

  /** Gets styles sources defined in the document. */
  private getDocumentStyleSources(filePath: string, headTagContent: string): StyleSource[] {
    const data: StyleSource[] = [];

    const html = new DOMParser().parseFromString(headTagContent, 'text/html');
    const head = html.head;
    if (head)
      Array.from(head.children).forEach((element) => {
        switch (element.tagName.toLowerCase()) {
          case 'link':
            const url = (element as HTMLLinkElement).getAttribute('href');  // use getAttribute() to preserve relative paths
            if (this.projectFileService.getFileType(url) === ProjectFileType.Stylesheet)
              data.push(this.createStyleSource(resolvePath(filePath, url)));
            break;
          case 'style':
            const content = (element as HTMLStyleElement).innerHTML.trim();
            if (content)
              data.push(this.createStyleSource(null, null, content));
            break;
        }
      });

    return data;
  }

  /** Get the observable for the project file document. Delegates to the app-wide cache. */
  private getFlareProjectFile$(projectId: number, commitIdOrBranchName: string, fileType: CollabFileType): Observable<HTMLElement> {
    return this.projectStylesheetCacheService.getFlareProjectFile$(projectId, commitIdOrBranchName, fileType);
  }

  /** Gets project style sources. */
  private getProjectStyleSources(projectDoc: HTMLElement): StyleSource[] {
    const links: string[] = [];

    if (projectDoc) {
      const brandingStylesheet = projectDoc.getAttribute('BrandingStylesheet');
      if (brandingStylesheet) {
        links.push(brandingStylesheet);
      }

      const masterStylesheet = projectDoc.getAttribute('MasterStylesheet');
      if (masterStylesheet) {
        links.push(masterStylesheet);
      }
    }

    return links
      .map(link => link.replace(/^\/+/g, ''))     // remove leading slashes
      .map(link => this.createStyleSource(link)); // map to StyleSource
  }

  /** Gets the observable for all style sources applied to the document. */
  private getStyleSources$(projectId: number, commitIdOrBranchName: string, fileType: CollabFileType, filePath: string, doc: ProseMirrorNode): Observable<StyleSource[]> {
    const documentProperties$ = merge(of(doc), this.documentSubject).pipe(
      map((doc): DocumentStyleOptions => this.getDocumentStyleOptions(doc)),
      distinctUntilChanged((a, b) => a.onlyLocalStylesheets === b.onlyLocalStylesheets && a.headTagContent === b.headTagContent),
      // Prevent bouncing when the document head changes rapidly
      debounceTime(10)
    );

    const projectDoc$ = this.reloadSubject.pipe(
      switchMap(() => this.getFlareProjectFile$(projectId, commitIdOrBranchName, fileType)),
      cache()
    );

    return combineLatest([
      documentProperties$,
      projectDoc$
    ]).pipe(
      map(([options, projectDoc]) => {
        // get options to get style sources related to the document
        const onlyLocalStyles = options.onlyLocalStylesheets;
        const allowOverrideMasterStylesheet = this.projectAllowsOverrideMasterStylesheet(projectDoc);
        // get style sources related to the document
        const projectStyleSources = !allowOverrideMasterStylesheet || !onlyLocalStyles
          ? this.getProjectStyleSources(projectDoc)
          : [];
        const documentStyleSources = allowOverrideMasterStylesheet
          ? this.getDocumentStyleSources(filePath, options.headTagContent)
          : [];
        // combine style sources related to the document
        const resultStyleSources = [
          ...projectStyleSources,
          ...documentStyleSources
        ];
        return { projectId, commitIdOrBranchName, fileType, filePath, resultStyleSources };
      }),
      switchMap(({ projectId, commitIdOrBranchName, fileType, filePath, resultStyleSources }) => {
        // process style sources to load their contents and nested @import stylesheets
        return this.processDocumentStyleSources$(projectId, commitIdOrBranchName, fileType, filePath, resultStyleSources, new Set<string>(), []);
      })
    );
  }

  /** Loads the content of the specified file. Delegates to the app-wide cache. */
  private loadFileContent$(projectId: number, commitIdOrBranchName: string, fileType: CollabFileType, filePath: string): Observable<string> {
    return this.projectStylesheetCacheService.loadFileContent$(projectId, commitIdOrBranchName, fileType, filePath);
  }

  /** Recursively processes document style sources to load their contents and nested stylesheets. */
  private processDocumentStyleSources$(projectId: number, commitIdOrBranchName: string, fileType: CollabFileType, filePath: string, pending: StyleSource[], processed: Set<string>, result: StyleSource[]): Observable<StyleSource[]> {
    // base case: no more pending items
    if (pending.length === 0) {
      return of(result);
    }
    // get the next item from the end of the pending list
    const styleSource = pending.pop();
    // if the style source has already been processed
    if (processed.has(styleSource.path)) {
      // continue with the next pending item
      return this.processDocumentStyleSources$(projectId, commitIdOrBranchName, fileType, filePath, pending, processed, result);
    }
    const loadedStyleSource$: Observable<StyleSource> = styleSource.css
      ? of(styleSource)
      : styleSource.path ?
        this.loadFileContent$(projectId, commitIdOrBranchName, fileType, styleSource.path).pipe( // load the content of the style source
          tap(() => processed.add(styleSource.path)), // mark the style source as processed
          map(content => {
            return this.createStyleSource(styleSource.path, styleSource.mediaQueries, content, filePath);
          })
        )
        : of(null); // case when neither content nor path is defined

    return loadedStyleSource$.pipe(
      switchMap(loadedStyleSource => {
        // if the style source has been loaded successfully
        if (loadedStyleSource?.css) {
          // add the loaded style source to the result
          result.unshift(loadedStyleSource);
          // extract @import links from the loaded content and add them to the pending list
          const imports = CssUtils.extractImports(loadedStyleSource.css);
          imports.forEach(data => {
            pending.push(this.createStyleSource(resolvePath(styleSource.path, data.link), data.mediaQueries));
          });
        }
        // continue with the next pending item
        return this.processDocumentStyleSources$(projectId, commitIdOrBranchName, fileType, filePath, pending, processed, result);
      })
    );
  }

  /** Gets 'MasterStylesheetOverride' option from the project document.  */
  private projectAllowsOverrideMasterStylesheet(projectDoc: HTMLElement): boolean {
    return projectDoc
      ? projectDoc.getAttribute('MasterStylesheetOverride')?.toLowerCase() === 'true'
      : true;
  }

  /** Updates css styles to preview the topic available styles. */
  private updatePreviewStyle(projectId: number, commitIdOrBranchName: string, filePath: string, filePathHex: string, computed: ComputedStylesheet): void {
    let content = this.computedStylesheetService.getStylesheetContent(computed, (style: ComputedStyle) => {
      // patch selector
      {
        const previewContainerId = CssUtils.toValidCssClassName(this.textEditorService.buildPreviewStylesheetContainerId(filePathHex));
        const selectorPrefix = `#${previewContainerId} .mc-preview-item`;
        style.name = patchSelectorForLwePreviewStylesheet(style.name, selectorPrefix);
      }
      // patch properties
      {
        const filteredProperties = {};
        Object.values(style.properties).forEach(property => {
          if (isLwePreviewStyleProperty(property.name))
            filteredProperties[property.name] = property;
        });
        style.properties = filteredProperties;
      }
      return style;
    });

    content = CssUtils.updateLinksInCss(content, (link) => {
      return this.textEditorService.buildFlareContentApiUri(projectId, commitIdOrBranchName, filePath, link);
    });

    this.previewStylesheetId = this.textEditorService.buildPreviewStylesheetId(filePathHex);
    this.styleService.create(this.previewStylesheetId, content);
  }

  private updateEditorComputedStyle(projectId: number, commitIdOrBranchName: string, filePath: string, filePathHex: string, computed: ComputedStylesheet): void {
    let content = this.computedStylesheetService.getStylesheetContent(computed, (style: ComputedStyle) => {
      // patch selector
      {
        const stylesheetContainerId = CssUtils.toValidCssClassName(this.textEditorService.buildComputedStylesheetId(filePathHex));
        const selectorPrefix = `.${stylesheetContainerId}.mc-prosemirror-schema-flare`;
        style.name = patchSelectorForLweEditorStylesheet(style.name, selectorPrefix);
      }
      // patch properties
      Object.values(style.properties).forEach(property => {
        property.name = toLweEditorStylePropertyName(property.name);
      });

      return style;
    });

    content = CssUtils.updateLinksInCss(content, (link) => {
      return this.textEditorService.buildFlareContentApiUri(projectId, commitIdOrBranchName, filePath, link);
    });

    // Scope the stylesheet to the the editor content but excluding embedded editor's within snippet node views
    // This prevents the computed styles from affecting the editor UI in the snippet node view
    content = CssUtils.wrapInScope(content, `mc-pm-editor`, 'mc-flare-text-editor, .cm-editor');

    this.computedStylesheetId = this.textEditorService.buildComputedStylesheetId(filePathHex);
    this.styleService.create(this.computedStylesheetId, content);
  }

}
